/**
 * IntegrationRow — the SINGLE source of truth for one integration row, shared by
 * BOTH the marketplace deploy modal and the deployed-agent settings list
 * (IntegrationsConnectList) so the two always look + behave identically.
 *
 * Visual contract:
 *   - Name area carries a small inline qualifier (like the "optional" tag):
 *       · required  → a lock icon (this agent depends on it; can't be turned off)
 *       · connected → the word "connected" (it's wired up account-wide)
 *       · optional  → the word "optional" (an optional integration, not connected)
 *   - Right side:
 *       · connected → a checkmark that is ALWAYS shown — GREEN when this row is
 *         selected to deploy/use for this agent, GREY when connected but not
 *         selected. (Read-only mode is always green = "connected".)
 *       · not connected → a "Connect" button.
 *   - selectable + connected + not-required → the whole row toggles on click.
 *   - A selected row also highlights its whole box green (.is-selected).
 *
 * Props:
 *   id, name, connectPath  — provider identity + where "Connect" goes.
 *   connected   — is the integration connected for this account/workflow.
 *   selectable  — selection (allowlist) mode. false → read-only (no toggle).
 *   selected    — is it enabled for THIS agent (drives green vs grey tick + box).
 *   locked      — required: always-on, not toggleable (shows the lock).
 *   optional    — show the "optional" tag when NOT connected.
 *   flashed     — deploy-gate "you must connect this" flash (modal only).
 *   onToggle(id), onConnect(id, connectPath) — handlers.
 */

import React from 'react';
import { IonIcon } from '@ionic/react';
import { checkmarkCircleOutline, lockClosedOutline } from 'ionicons/icons';
import IntegrationLogo from '../IntegrationLogo';

const IntegrationRow = ({
  id,
  name,
  connectPath = '/integrations',
  connected = false,
  selectable = false,
  selected = false,
  locked = false,
  optional = false,
  flashed = false,
  // No-connection toggleable skill (e.g. codebase-memory): a skill that needs NO
  // integration/token. Renders as a toggle-ONLY row — there is no "Connect"
  // button and no "Connected" account state; it's always a candidate, the row
  // just turns it on/off for THIS agent (reusing the SAME enabledIntegrations
  // allowlist). `description` shows as a small sub-line. Only meaningful in
  // selectable mode (the read-only trigger modal never sets selectable).
  noIntegration = false,
  description = '',
  onToggle,
  onConnect,
}) => {
  // ── No-connection toggleable skill row (toggle-only; no Connect / Connected) ──
  if (noIntegration) {
    const isOn = !selectable || selected; // read-only renders as "on"
    const right = (
      <>
        <span className={`mkt-modal-integration-connected-label${isOn ? '' : ' mkt-modal-integration-connected-label--off'}`}>
          {isOn ? 'On' : 'Off'}
        </span>
        <IonIcon
          icon={checkmarkCircleOutline}
          className={`mkt-modal-integration-connected-icon${isOn ? '' : ' mkt-modal-integration-connected-icon--off'}`}
          aria-label={isOn ? 'Enabled for this agent' : 'Disabled for this agent'}
        />
      </>
    );
    const brandWithDesc = (
      <span className="mkt-modal-integration-brand">
        <IntegrationLogo id={id} size={18} title={name} />
        <span className="mkt-modal-integration-name">
          {name}
          {description ? (
            <span className="mkt-modal-integration-desc">{description}</span>
          ) : null}
        </span>
      </span>
    );
    if (!selectable) {
      return (
        <li className="mkt-modal-integration-row is-connected">
          {brandWithDesc}
          <span className="mkt-modal-integration-state">{right}</span>
        </li>
      );
    }
    const cls = [
      'mkt-modal-integration-row',
      'is-connected',
      selected ? 'is-selected' : 'is-deselected',
      'is-togglable',
    ].join(' ');
    return (
      <li
        className={cls}
        role="button"
        tabIndex={0}
        aria-pressed={selected}
        title={selected ? 'Enabled for this agent — click to turn off' : 'Click to enable for this agent'}
        onClick={() => onToggle?.(id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle?.(id); }
        }}
      >
        {brandWithDesc}
        <span className="mkt-modal-integration-state">{right}</span>
      </li>
    );
  }

  // Inline name qualifier: lock for required, "optional" for optional (shown
  // whether or not it's connected). "connected" is NOT here — it lives on the
  // RIGHT, in the same slot as the "Connect" button, for consistency.
  let nameTag = null;
  if (locked) {
    nameTag = (
      <IonIcon icon={lockClosedOutline} className="mkt-modal-integration-name-lock" aria-label="Required" />
    );
  } else if (optional) {
    nameTag = <span className="mkt-modal-integration-optional-tag">optional</span>;
  }

  const brand = (
    <span className="mkt-modal-integration-brand">
      <IntegrationLogo id={id} size={18} title={name} />
      <span className="mkt-modal-integration-name">{name}</span>
      {nameTag}
    </span>
  );

  // CONNECTED → always-on checkmark, green when selected (read-only counts as
  // selected = "connected"), grey when connected-but-not-selected-for-this-agent.
  if (connected) {
    const isOn = !selectable || selected || locked;
    // Right side mirrors the "Connect" slot: the word "Connected" + an always-on
    // checkmark. Both GREEN when used by this agent, GREY when connected-but-not-
    // used — text colour tracks the tick.
    const right = (
      <>
        <span className={`mkt-modal-integration-connected-label${isOn ? '' : ' mkt-modal-integration-connected-label--off'}`}>
          Connected
        </span>
        <IonIcon
          icon={checkmarkCircleOutline}
          className={`mkt-modal-integration-connected-icon${isOn ? '' : ' mkt-modal-integration-connected-icon--off'}`}
          aria-label={isOn ? 'Enabled for this agent' : 'Connected — not used by this agent'}
        />
      </>
    );
    if (!selectable) {
      return (
        <li className="mkt-modal-integration-row is-connected">
          {brand}
          <span className="mkt-modal-integration-state">{right}</span>
        </li>
      );
    }
    const cls = [
      'mkt-modal-integration-row',
      'is-connected',
      selected || locked ? 'is-selected' : 'is-deselected',
      locked ? 'is-locked' : 'is-togglable',
    ].join(' ');
    return (
      <li
        className={cls}
        role="button"
        tabIndex={locked ? -1 : 0}
        aria-pressed={selected || locked}
        aria-disabled={locked || undefined}
        title={locked
          ? 'Required — always enabled for this agent'
          : selected
            ? 'Enabled for this agent — click to turn off'
            : 'Click to let this agent use it'}
        onClick={() => !locked && onToggle?.(id)}
        onKeyDown={(e) => {
          if (locked) return;
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle?.(id); }
        }}
      >
        {brand}
        <span className="mkt-modal-integration-state">{right}</span>
      </li>
    );
  }

  // NOT CONNECTED → Connect button (+ deploy-gate flash on the modal).
  const rowCls = `mkt-modal-integration-row ${optional ? 'is-optional' : 'is-missing'}${flashed ? ' is-flashed' : ''}`;
  return (
    <li className={rowCls}>
      {brand}
      <span className="mkt-modal-integration-state">
        <button
          type="button"
          className={optional
            ? 'mkt-modal-integration-connect mkt-modal-integration-connect--optional'
            : 'mkt-modal-integration-connect'}
          onClick={(e) => { e.stopPropagation(); onConnect?.(id, connectPath); }}
        >
          Connect
        </button>
        {/* Grey tick for visual consistency with connected rows — same column,
            just greyed since this integration isn't connected/used yet. */}
        <IonIcon
          icon={checkmarkCircleOutline}
          className="mkt-modal-integration-connected-icon mkt-modal-integration-connected-icon--off"
          aria-hidden="true"
        />
      </span>
    </li>
  );
};

export default IntegrationRow;
